import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  getSortedRowModel,
  type SortingState,
  type VisibilityState,
  type Row,
} from '@tanstack/react-table'
import { type ReactNode, useMemo, useState } from 'react'
import { GenericTableMobileSortButton } from './GenericTableMobileSort'
import { GenericTableMobileFooter } from './GenericTableMobileFooter'
import { Card } from '../Card'
import { Loader } from '../Loader'
import { SortingArrowsIcon } from '../../../icons/SortingArrowsIcon'
import { useMediaQuery } from '../../../hooks/useMediaQuery'
import { useUiLabels } from '../../../labels'

export interface ColumnMeta {
  alignmentType?: 'left' | 'right'
  mobileFooterLabel?: string
}

type ColumnWidthProps = {
  width?: number
  minWidth?: number
  maxWidth?: number
}

type GenericTableProps<T> = {
  className?: string
  isLoading?: boolean
  data: T[]
  mobileCardContent: (row: Row<T>, onRowClick?: (row: Row<T>) => void) => ReactNode
  mobileToolbar?: ReactNode
  columns: ColumnDef<T, any>[]
  columnVisibility?: VisibilityState
  columnWidths?: Record<string, ColumnWidthProps>
  defaultColumnWidth?: ColumnWidthProps
  onColumnVisibilityChange?: (updater: VisibilityState | ((old: VisibilityState) => VisibilityState)) => void
  onRowClick?: (row: Row<T>) => void
  tableMeta?: ColumnMeta
}

const GenericTable = <T,>({
  data,
  columns,
  columnVisibility,
  onColumnVisibilityChange,
  tableMeta,
  isLoading,
  columnWidths,
  defaultColumnWidth,
  mobileCardContent,
  mobileToolbar,
  onRowClick,
  className,
}: GenericTableProps<T>) => {
  const labels = useUiLabels()
  const [sorting, setSorting] = useState<SortingState>([])

  const columnsWidth = useMemo(() => {
    const widths = columnWidths ?? {}

    return columns.map((col: any) => {
      const id = col.id ?? col.accessorKey
      if (!id) return col

      const width = widths[String(id)]
      if (!width) return col

      return {
        ...col,
        size: width.width ?? col.width,
        minSize: width.minWidth ?? col.minWidth,
        maxSize: width.maxWidth ?? col.maxWidth,
      }
    })
  }, [columns, columnWidths])

  const table = useReactTable({
    data,
    columns: columnsWidth,
    defaultColumn: {
      size: defaultColumnWidth?.width ?? 150,
      minSize: defaultColumnWidth?.width ?? 80,
      maxSize: defaultColumnWidth?.width ?? 300,
    },
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    autoResetPageIndex: false,
    meta: tableMeta,
  })

  const isDesktop = useMediaQuery('(min-width: 768px)')
  const hasData = table.getRowCount() !== 0
  const hasFooter = table.getAllLeafColumns().some((column) => column.columnDef.footer !== undefined)

  return (
    <Card
      background="white"
      className={`${className} ${isDesktop ? 'ap:px-4 ap:overflow-y-auto' : 'ap:border-none ap:overflow-visible ap:max-h-max ap:shadow-none'} ap:w-full ap:animate-popUpFromBottom`}
    >
      {!isDesktop && (hasData || !!mobileToolbar) && (
        <div className="ap:flex ap:items-center ap:justify-end ap:gap-md">
          {mobileToolbar && <div className="ap:min-w-0 ap:flex-1">{mobileToolbar}</div>}
          {hasData && <GenericTableMobileSortButton<T> table={table} />}
        </div>
      )}
      {hasData && (
        <div className="ap:md:hidden">
          <GenericTableMobileFooter<T> table={table} />
        </div>
      )}
      <table className="ap:w-full ap:table-fixed ap:py-2">
        <thead className="ap:hidden ap:md:table-header-group ap:bg-content-inverse ap:sticky ap:top-0 ap:z-2 ap:h-12">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="ap:pt-4 ap:px-4">
              {group.headers.map((header) => {
                const meta = header.column.columnDef.meta as ColumnMeta | undefined
                const alignment = meta?.alignmentType === 'right' ? 'ap:justify-end' : 'ap:justify-start'

                return (
                  <th
                    key={header.id}
                    style={{ width: header.getSize() !== 150 ? `${header.getSize()}px` : undefined }}
                    className={`${alignment} ap:text-primaryText ap:text-sm ap:px-4 ap:py-2 ap:font-semibold ap:text-nowrap ap:overflow-hidden ap:text-ellipsis ap:whitespace-nowrap ${
                      header.column.getCanSort() ? 'ap:cursor-pointer' : ''
                    }`}
                    onClick={header.column.getToggleSortingHandler?.()}
                  >
                    {header.isPlaceholder ? null : (
                      <div className={`ap:flex ap:items-center ${alignment} ap:gap-1`}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        <div className="ap:scale-75">
                          {header.column.getCanSort() &&
                            {
                              asc: <SortingArrowsIcon sorting="asc" />,
                              desc: <SortingArrowsIcon sorting="desc" />,
                              false: <SortingArrowsIcon sorting="none" />,
                            }[String(header.column.getIsSorted()) as 'asc' | 'desc' | 'false']}
                        </div>
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowCount() === 0 && (
            <tr>
              <td colSpan={table.getVisibleFlatColumns().length}>
                <div className="ap:py-8 ap:flex ap:flex-col ap:items-center ap:justify-center ap:gap-2">
                  {isLoading && (
                    <>
                      <Loader />
                      <p>{labels.loading}</p>
                    </>
                  )}
                  {!isLoading && <div>{labels.noData}</div>}
                </div>
              </td>
            </tr>
          )}
          {table.getRowModel().rows.map((row, index) => {
            return (
              <TableRow<T>
                key={row.id}
                row={row}
                rowIndex={index}
                mobileCardContent={mobileCardContent}
                onRowClick={onRowClick}
                isDesktop={isDesktop}
              />
            )
          })}
        </tbody>

        {hasFooter && (
          <tfoot className="ap:hidden ap:md:table-footer-group ap:sticky ap:bottom-0 ap:bg-content-inverse">
            {table.getFooterGroups().map((footerGroup) => (
              <tr key={footerGroup.id} className="ap:h-12 ap:font-bold ap:text-sm">
                {footerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta as ColumnMeta
                  const alignment = meta?.alignmentType === 'right' ? 'ap:text-right' : 'ap:text-left'

                  return (
                    <td key={header.id} className={`ap:px-4 ${alignment}`}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tfoot>
        )}
      </table>
    </Card>
  )
}

export default GenericTable

function TableRow<T>({
  row,
  rowIndex,
  mobileCardContent,
  onRowClick,
  isDesktop,
}: {
  row: Row<T>
  rowIndex: number
  mobileCardContent: (row: Row<T>, onRowClick?: (row: Row<T>) => void) => ReactNode
  onRowClick?: (row: Row<T>) => void
  isDesktop: boolean
}) {
  if (!isDesktop) {
    return (
      <tr key={rowIndex}>
        <td colSpan={row.getVisibleCells().length}>
          {<TableCard row={row} content={mobileCardContent} onRowClick={onRowClick} />}
        </td>
      </tr>
    )
  }

  return (
    <tr
      key={rowIndex}
      className={`${onRowClick && 'ap:cursor-pointer'} ap:h-12 ap:font-normal ap:text-sm ap:text-content-primary ap:hover:font-semibold ap:odd:bg-surface-primary ap:hover:bg-surface-primary-hover ap:group`}
      onClick={onRowClick ? () => onRowClick(row) : undefined}
    >
      {row.getVisibleCells().map((cell, index) => {
        const meta = cell.column.columnDef.meta as ColumnMeta
        const alignment = meta?.alignmentType === 'right' ? 'ap:text-right' : 'ap:text-left'

        return (
          <td
            key={`${index}_${cell.id}`}
            style={{ width: `${cell.column.getSize()}px` }}
            className={`ap:px-4 ap:overflow-hidden ap:text-ellipsis ap:whitespace-nowrap ${alignment}`}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        )
      })}
    </tr>
  )
}

function TableCard<T>({
  row,
  content,
  onRowClick,
}: {
  row: Row<T>
  content: (row: Row<T>, onRowClick?: (row: Row<T>) => void) => ReactNode
  onRowClick?: (row: Row<T>) => void
}) {
  return (
    <Card background="white" className="ap:w-full ap:mb-4">
      {content(row, onRowClick)}
    </Card>
  )
}
